import { useState } from "react";
import { styled } from "@mui/material/styles";
import { Typography, Box } from "@mui/material";
import useResponsive from "../hooks/useResponsive";
import { LoginForm } from "../sections/auth/login";
import Logo from "../components/Logo";
import { Helmet } from "react-helmet-async";
import SimpleFooter from "../components/SimpleFooter";

const welcomeHeadings = [
  "Hi! Welcome back.",
  "Hey! Good to see you.",
  "Hello! Glad you're here.",
  "Hi there! Welcome back.",
  "Hey! Nice to see you again.",
  "Hello! Ready to get started?",
  "Hi! Great to see you.",
  "Hey there! Welcome back.",
  "Hello! Let's get going.",
  "Hi! Your profile is ready.",
];

const welcomeSubheadings = [
  "Build trust with every interaction.",
  "Grow your reputation and get noticed.",
  "Connect, share, and grow your profile.",
  "Let people see your real work.",
  "Your ratings show your value.",
  "Make every deal count.",
  "Stand out with real credibility.",
  "Get discovered and trusted.",
  "Work smart, get rated, get noticed.",
  "Let your profile do the talking.",
];

const getRandomMessages = () => {
  const heading =
    welcomeHeadings[Math.floor(Math.random() * welcomeHeadings.length)];
  const subheading =
    welcomeSubheadings[Math.floor(Math.random() * welcomeSubheadings.length)];
  return { heading, subheading };
};

const StyledPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",

  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const StyledSection = styled("div")(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: {
    width: "40%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3, 4),
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledContent = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  padding: theme.spacing(8, 3, 2, 3),
  overflowY: "auto",

  [theme.breakpoints.up("md")]: {
    width: "35%",
    alignItems: "center",
    padding: theme.spacing(3, 4),
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 480,

  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(6, 5),
    boxShadow: theme.shadows[8],
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

export default function LoginPage() {
  const mdUp = useResponsive("up", "md");
  const [welcomeMessages] = useState(getRandomMessages);

  return (
    <>
      <Helmet>
        {/* --- Standard SEO --- */}
        <title>Sign In | Contactie</title>
        <meta
          name="description"
          content="Log in to your Contactie account to manage your profile, connect with professionals, and build your reputation."
        />
        <link rel="canonical" href="https://contactie.com/Login" />

        {/* --- Open Graph (for Facebook, LinkedIn, etc.) --- */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Contactie" />
        <meta property="og:title" content="Sign In | Contactie" />
        <meta
          property="og:description"
          content="Log in to your Contactie account to manage your professional profile and network."
        />
        <meta
          property="og:image"
          content="https://contactie.com/track%20page.png"
        />
        <meta property="og:url" content="https://contactie.com/Login" />

        {/* --- Twitter Card --- */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sign In | Contactie" />
        <meta
          name="twitter:description"
          content="Log in to your Contactie account to manage your professional profile and network."
        />
        {/* --- IMPORTANT: Add your site's Twitter handle here --- */}
        <meta name="twitter:site" content="@contactie" />
        <meta
          name="twitter:image"
          content="https://contactie.com/track%20page.png"
        />

        {/* --- Structured Data (JSON-LD) for Google --- */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Contactie Login",
              "description": "Securely access your Contactie account.",
              "url": "https://contactie.com/Login",
              "isPartOf": {
                "@type": "WebSite",
                "name": "Contactie",
                "url": "https://contactie.com/"
              }
            }
          `}
        </script>
      </Helmet>

      <StyledPageContainer>
        <ContentWrapper>
          <Box
            component="header"
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              zIndex: 10,
              top: 40,
              left: 40,
            }}
          >
            <Logo sx={{ height: 40, width: "auto" }} />
          </Box>

          {mdUp && (
            <StyledSection>
              <Typography
                variant="h4"
                sx={{ textAlign: "center", mb: 2, px: 5 }}
              >
                {welcomeMessages.heading}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", textAlign: "center" }}
              >
                {welcomeMessages.subheading}
              </Typography>
              <Box
                component="img"
                src="/assets/illustrations/illustration_login.svg"
                alt="The power of connection, across the planet."
                sx={{
                  width: "100%",
                  maxWidth: 360,
                  maxHeight: "60vh",
                  objectFit: "contain",
                  mt: 4,
                }}
              />
            </StyledSection>
          )}

          <StyledContent>
            <FormContainer>
              <Box
                sx={{
                  mb: 5,
                  display: { xs: "flex", md: "none" },
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Logo sx={{ height: 40, width: "auto", mb: 3 }} />
                <Typography variant="h4" gutterBottom>
                  {welcomeMessages.heading}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {welcomeMessages.subheading}
                </Typography>
              </Box>

              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                  Sign in to Contactie
                </Typography>
              </Box>

              <LoginForm />
            </FormContainer>
          </StyledContent>
        </ContentWrapper>
        <SimpleFooter />
      </StyledPageContainer>
    </>
  );
}
